import { Checkbox, Popconfirm } from 'antd';
import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import {
  setTableColumnSelection,
  clearAzureReservationsMessages,
  azureReservationsSelector,
} from '../../../../store/azure/azureReservations/azureReservations.reducer';
import { useAppDispatch, useAppSelector } from '../../../../store/app.hooks';
import {
  deleteAzureReservations,
  searchAzureReservations,
} from '../../../../store/azure/azureReservations/azureReservations.action';
import _ from 'lodash';
import azureReservationsService from '../../../../services/azure/azureReservations/azureReservations.service';
import {
  FilterByBooleanDropDown,
  FilterByDateSwap,
  FilterWithSwapOption,
} from '../../../../common/components/DataTable/DataTableFilters';
import { IMainTable, ISearch } from '../../../../common/models/common';
import { useHistory } from 'react-router-dom';
import DataTable from '../../../../common/components/DataTable';
import ability, { Can } from '../../../../common/ability';
import { Action, Page } from '../../../../common/constants/pageAction';
import { showDateFromApi } from '../../../../common/helperFunction';
import { AlignType } from 'rc-table/lib/interface';

const MainTable: React.ForwardRefRenderFunction<unknown, IMainTable> = (props, ref) => {
  const {
    setSelectedId,
    setShowSelectedListModal,
    setValuesForSelection,
    isMultiple,
    tableButtons,
  } = props;
  const azureReservations = useAppSelector(azureReservationsSelector);
  const dispatch = useAppDispatch();
  const dataTableRef = useRef(null);
  const history = useHistory();
  const [ObjectForColumnFilter, setObjectForColumnFilter] = useState({});

  useImperativeHandle(ref, () => ({
    refreshData() {
      dataTableRef?.current.refreshData();
    },
  }));

  useEffect(() => {
    if (isMultiple) {
      dataTableRef?.current.getValuesForSelection();
    }
  }, [isMultiple]);

  const exportExcelFile = (searchData: ISearch) => {
    return azureReservationsService.exportExcelFile(searchData);
  };

  const FilterBySwap = (dataIndex: string, form, isRelativeDropDownFlag?: boolean) => {
    return FilterWithSwapOption(
      dataIndex,
      azureReservations.search.tableName,
      form,
      null,
      ObjectForColumnFilter,
      null,
      null,
      isRelativeDropDownFlag
    );
  };

  const FilterByDateSwapTable = (dataIndex: string, tableName: string, form: any) => {
    return FilterByDateSwap(dataIndex, tableName, form, null, ObjectForColumnFilter);
  };

  const getTableColumns = (form) => {
    return [
      {
        title: <span className="dragHandler">ID</span>,
        column: 'id',
        sorter: true,
        ellipsis: true,
        children: [
          {
            title: FilterBySwap('id', form),
            dataIndex: 'id',
            key: 'id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Tenant Name</span>,
        column: 'TenantId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('tenant_id', form, true),
            dataIndex: 'tenant_name',
            key: 'tenant_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Company Name</span>,
        column: 'CompanyId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('company_id', form, true),
            dataIndex: 'company_name',
            key: 'company_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Bu Name</span>,
        column: 'BU_Id',
        sorter: true,
        children: [
          {
            title: FilterBySwap('bu_id', form, true),
            dataIndex: 'bu_name',
            key: 'bu_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Date Added</span>,
        column: 'Date Added',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable('date_added', azureReservations.search.tableName, form),
            dataIndex: 'date_added',
            key: 'date_added',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Reservation Order Id</span>,
        column: 'ReservationOrderId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('reservation_order_id', form),
            dataIndex: 'reservation_order_id',
            key: 'reservation_order_id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Reservation Id</span>,
        column: 'ReservationId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('reservation_id', form),
            dataIndex: 'reservation_id',
            key: 'reservation_id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Resource Id</span>,
        column: 'ResourceId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('resource_id', form),
            dataIndex: 'resource_id',
            key: 'resource_id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Display Name</span>,
        column: 'DisplayName',
        sorter: true,
        children: [
          {
            title: FilterBySwap('display_name', form),
            dataIndex: 'display_name',
            key: 'display_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Sku Name</span>,
        column: 'SkuName',
        sorter: true,
        children: [
          {
            title: FilterBySwap('sku_name', form),
            dataIndex: 'sku_name',
            key: 'sku_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Sku Description</span>,
        column: 'SkuDescription',
        sorter: true,
        children: [
          {
            title: FilterBySwap('sku_description', form),
            dataIndex: 'sku_description',
            key: 'sku_description',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Location</span>,
        column: 'Location',
        sorter: true,
        children: [
          {
            title: FilterBySwap('location', form),
            dataIndex: 'location',
            key: 'location',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Reserved Resource Type</span>,
        column: 'ReservedResourceType',
        sorter: true,
        children: [
          {
            title: FilterBySwap('reserved_resource_type', form),
            dataIndex: 'reserved_resource_type',
            key: 'reserved_resource_type',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Quantity</span>,
        column: 'Quantity',
        sorter: true,
        children: [
          {
            title: FilterBySwap('quantity', form),
            dataIndex: 'quantity',
            key: 'quantity',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Term</span>,
        column: 'Term',
        sorter: true,
        children: [
          {
            title: FilterBySwap('term', form),
            dataIndex: 'term',
            key: 'term',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Billing Plan</span>,
        column: 'BillingPlan',
        sorter: true,
        children: [
          {
            title: FilterBySwap('billing_plan', form),
            dataIndex: 'billing_plan',
            key: 'billing_plan',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Billing Scope Id</span>,
        column: 'BillingScopeId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('billing_scope_id', form),
            dataIndex: 'billing_scope_id',
            key: 'billing_scope_id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Applied Scope Type</span>,
        column: 'AppliedScopeType',
        sorter: true,
        children: [
          {
            title: FilterBySwap('applied_scope_type', form),
            dataIndex: 'applied_scope_type',
            key: 'applied_scope_type',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">User Friendly Applied Scope Type</span>,
        column: 'UserFriendlyAppliedScopeType',
        sorter: true,
        children: [
          {
            title: FilterBySwap('user_friendly_applied_scope_type', form),
            dataIndex: 'user_friendly_applied_scope_type',
            key: 'user_friendly_applied_scope_type',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Applied Scope Display Name</span>,
        column: 'AppliedScopeDisplayName',
        sorter: true,
        children: [
          {
            title: FilterBySwap('applied_scope_display_name', form),
            dataIndex: 'applied_scope_display_name',
            key: 'applied_scope_display_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Applied Scope Subscription Id</span>,
        column: 'AppliedScopeSubscriptionId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('applied_scope_subscription_id', form),
            dataIndex: 'applied_scope_subscription_id',
            key: 'applied_scope_subscription_id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Applied Scope Resource Group Id</span>,
        column: 'AppliedScopeResourceGroupId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('applied_scope_resource_group_id', form),
            dataIndex: 'applied_scope_resource_group_id',
            key: 'applied_scope_resource_group_id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Applied Scope Management Group Id</span>,
        column: 'AppliedScopeManagementGroupId',
        sorter: true,
        children: [
          {
            title: FilterBySwap('applied_scope_management_group_id', form),
            dataIndex: 'applied_scope_management_group_id',
            key: 'applied_scope_management_group_id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Applied Scopes</span>,
        column: 'AppliedScopes',
        sorter: true,
        children: [
          {
            title: FilterBySwap('applied_scopes', form),
            dataIndex: 'applied_scopes',
            key: 'applied_scopes',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Instance Flexibility</span>,
        column: 'InstanceFlexibility',
        sorter: true,
        children: [
          {
            title: FilterBySwap('instance_flexibility', form),
            dataIndex: 'instance_flexibility',
            key: 'instance_flexibility',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Provisioning State</span>,
        column: 'ProvisioningState',
        sorter: true,
        children: [
          {
            title: FilterBySwap('provisioning_state', form),
            dataIndex: 'provisioning_state',
            key: 'provisioning_state',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Display Provisioning State</span>,
        column: 'DisplayProvisioningState',
        sorter: true,
        children: [
          {
            title: FilterBySwap('display_provisioning_state', form),
            dataIndex: 'display_provisioning_state',
            key: 'display_provisioning_state',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Archived</span>,
        column: 'Archived',
        sorter: true,
        children: [
          {
            title: FilterByBooleanDropDown(
              'archived',
              azureReservations.search.tableName,
              ObjectForColumnFilter
            ),
            dataIndex: 'archived',
            key: 'archived',
            ellipsis: true,
            render: (value: boolean) =>
              !_.isNull(value) ? (
                value ? (
                  <Checkbox checked disabled />
                ) : (
                  <Checkbox checked={false} disabled />
                )
              ) : (
                ''
              ),
            align: 'center' as AlignType,
          },
        ],
      },
      {
        title: <span className="dragHandler">Renew</span>,
        column: 'Renew',
        sorter: true,
        children: [
          {
            title: FilterByBooleanDropDown(
              'renew',
              azureReservations.search.tableName,
              ObjectForColumnFilter
            ),
            dataIndex: 'renew',
            key: 'renew',
            ellipsis: true,
            render: (value: boolean) =>
              !_.isNull(value) ? (
                value ? (
                  <Checkbox checked disabled />
                ) : (
                  <Checkbox checked={false} disabled />
                )
              ) : (
                ''
              ),
            align: 'center' as AlignType,
          },
        ],
      },
      {
        title: <span className="dragHandler">User Friendly Renew State</span>,
        column: 'UserFriendlyRenewState',
        sorter: true,
        children: [
          {
            title: FilterBySwap('user_friendly_renew_state', form),
            dataIndex: 'user_friendly_renew_state',
            key: 'user_friendly_renew_state',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Purchase Date</span>,
        column: 'PurchaseDate',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable('purchase_date', azureReservations.search.tableName, form),
            dataIndex: 'purchase_date',
            key: 'purchase_date',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Benefit Start Date</span>,
        column: 'BenefitStartDate',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable(
              'benefit_start_date',
              azureReservations.search.tableName,
              form
            ),
            dataIndex: 'benefit_start_date',
            key: 'benefit_start_date',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Effective Date</span>,
        column: 'EffectiveDate',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable(
              'effective_date',
              azureReservations.search.tableName,
              form
            ),
            dataIndex: 'effective_date',
            key: 'effective_date',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Expiry Date</span>,
        column: 'ExpiryDate',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable('expiry_date', azureReservations.search.tableName, form),
            dataIndex: 'expiry_date',
            key: 'expiry_date',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Utilization 1 Day %</span>,
        column: 'Utilization1Day',
        sorter: true,
        children: [
          {
            title: FilterBySwap('utilization_1_day', form),
            dataIndex: 'utilization_1_day',
            key: 'utilization_1_day',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Utilization 7 Day %</span>,
        column: 'Utilization7Day',
        sorter: true,
        children: [
          {
            title: FilterBySwap('utilization_7_day', form),
            dataIndex: 'utilization_7_day',
            key: 'utilization_7_day',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Utilization 30 Day %</span>,
        column: 'Utilization30Day',
        sorter: true,
        children: [
          {
            title: FilterBySwap('utilization_30_day', form),
            dataIndex: 'utilization_30_day',
            key: 'utilization_30_day',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Utilization Trend</span>,
        column: 'UtilizationTrend',
        sorter: true,
        children: [
          {
            title: FilterBySwap('utilization_trend', form),
            dataIndex: 'utilization_trend',
            key: 'utilization_trend',
            ellipsis: true,
          },
        ],
      },
    ];
  };

  const removeAzureReservations = (id: number) => {
    dispatch(deleteAzureReservations(id));
  };

  const tableAction = (_unused, data: any) => (
    <div className="btns-block">
      <Can I={Action.Update} a={Page.AzureReservations}>
        <a
          className="action-btn"
          onClick={() => {
            setSelectedId(data.id);
            history.push(`/azure/azure-reservations/${data.id}`);
          }}
        >
          <img src={`${process.env.PUBLIC_URL}/assets/images/ic-edit.svg`} alt="" />
        </a>
      </Can>
      <Can I={Action.Delete} a={Page.AzureReservations}>
        <Popconfirm title="Delete Record?" onConfirm={() => removeAzureReservations(data.id)}>
          <a href="#" title="" className="action-btn">
            <img src={`${process.env.PUBLIC_URL}/assets/images/ic-delete.svg`} alt="" />
          </a>
        </Popconfirm>
      </Can>
    </div>
  );

  return (
    <>
      <DataTable
        ref={dataTableRef}
        showAddButton={ability.can(Action.Add, Page.AzureReservations)}
        setSelectedId={setSelectedId}
        tableAction={tableAction}
        exportExcelFile={exportExcelFile}
        getTableColumns={getTableColumns}
        reduxSelector={azureReservationsSelector}
        searchTableData={searchAzureReservations}
        clearTableDataMessages={clearAzureReservationsMessages}
        setTableColumnSelection={setTableColumnSelection}
        setShowSelectedListModal={setShowSelectedListModal}
        setValuesForSelection={setValuesForSelection}
        setObjectForColumnFilter={setObjectForColumnFilter}
        tableButtons={tableButtons}
      />
    </>
  );
};

export default forwardRef(MainTable);
